import { useTranslation } from 'i18n';
import { useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogHeader,
  ImageCarousel,
  ImageCarouselMediaItem,
  Typography,
} from 'ui';

import {
  AiFeedbackVisibilityProvider,
  AiFiltersAndBreakdownSidebar,
  AnnotatedMediaItem,
  useAnnotations,
} from '../../../annotations';
import { getMaterialCategoryFromEvaluatedSpecs } from '../../../breakdowns/utils/getMaterialCategoryFromEvaluatedSpecs/getMaterialCategoryFromEvaluatedSpecs';
import { type PreviewGalleryImage } from '../../types';

export const PreviewImageReview = ({
  open,
  onClose,
  selectedImage,
  onDeleteImage,
  showDeleteButton = true,
  isPortrait,
  showAerosols = false,
  showAnnotations = true,
  showFilters = true,
}: {
  open: boolean;
  onClose: () => void;
  selectedImage: PreviewGalleryImage;
  onDeleteImage: () => void;
  showDeleteButton?: boolean;
  isPortrait: boolean;
  showAerosols?: boolean;
  showAnnotations?: boolean;
  showFilters?: boolean;
}) => {
  const { t } = useTranslation('features');
  const shouldShowMessage = selectedImage.status !== 'success' && selectedImage.message;
  const isSelectedImageRejected = selectedImage?.status !== 'success';

  const {
    handleImageChange,
    selectedAnnotation,
    hiddenCategories,
    showLegend,
    handleHideCategory,
    handleToggleLegend,
  } = useAnnotations({ initialAnnotations: selectedImage.annotations, model: selectedImage.aiModel ?? 'alu' });

  useEffect(() => {
    if (selectedImage.annotations) {
      handleImageChange(selectedImage.annotations);
    }
  }, [selectedImage.annotations, handleImageChange]);

  const showAnnotatedMediaItem = !!(showAnnotations && selectedAnnotation && selectedImage && selectedImage.aiModel);

  const showAIFiltersAndBreakdown = !!(
    selectedImage.status === 'success' &&
    selectedAnnotation &&
    selectedImage.aiModel &&
    showAnnotations &&
    showFilters
  );

  return (
    <AiFeedbackVisibilityProvider>
      <Dialog open={open} onClose={onClose} fullScreen={true} data-testid="preview-image-review">
        <DialogHeader title="" onClose={onClose} />
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: isPortrait ? 'column' : 'row',
            gap: 2,
          }}
        >
          <ImageCarousel
            photoEvidence={[selectedImage.src]}
            imageCount={1}
            slotProps={{
              mediaItem: {
                alt: t('cameraGuidance.previewImageReview.evidencePhotoAlt'),
              },
              mediaItemSlot: (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    {shouldShowMessage ? <Typography>{selectedImage.message}</Typography> : null}
                    {selectedImage.feedbackMessages?.length ? (
                      <ul>
                        {selectedImage.feedbackMessages?.map((message) => (
                          <li key={message}>
                            <Typography>{message}</Typography>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  {showAnnotatedMediaItem ? (
                    <AnnotatedMediaItem
                      image={selectedImage.src}
                      annotations={selectedAnnotation}
                      hiddenCategories={hiddenCategories}
                      showLegend={showLegend}
                      model={selectedImage.aiModel ?? 'alu'}
                      showAerosols={showAerosols}
                      evidenceId={Number(selectedImage.id)}
                    />
                  ) : (
                    <ImageCarouselMediaItem media={selectedImage.src} />
                  )}
                </Box>
              ),
              sidebar:
                // even if I already check this in showAIFiltersAndBreakdown,
                // I need to check again here because typescript doesn't know that selectedImage.aiModel is defined
                showAIFiltersAndBreakdown && selectedImage.aiModel
                  ? {
                      children: (
                        <AiFiltersAndBreakdownSidebar
                          selectedAnnotation={selectedAnnotation}
                          hiddenCategories={hiddenCategories}
                          evidenceId={Number(selectedImage.id)}
                          handleHideCategory={handleHideCategory}
                          handleToggleLegend={handleToggleLegend}
                          showLegend={showLegend}
                          model={selectedImage.aiModel}
                          showAerosols={showAerosols}
                          aluminiumComposition={
                            isSelectedImageRejected ? undefined : (selectedImage?.aluminiumComposition ?? undefined)
                          }
                          hdpeComposition={
                            isSelectedImageRejected ? undefined : (selectedImage?.hdpeComposition ?? undefined)
                          }
                          petComposition={
                            isSelectedImageRejected ? undefined : (selectedImage?.petComposition ?? undefined)
                          }
                          evaluatedSpecs={selectedImage.evaluatedSpecs ?? undefined}
                          materialCategory={getMaterialCategoryFromEvaluatedSpecs(selectedImage.evaluatedSpecs)}
                        />
                      ),
                    }
                  : undefined,
            }}
          />
        </DialogContent>
        {showDeleteButton ? (
          <DialogActions sx={{ justifyContent: 'space-between', padding: 2 }}>
            <Button onClick={onDeleteImage} disabled={selectedImage?.disableDelete} fullWidth>
              {t('cameraGuidance.previewImageReview.actions.deleteAndRetake')}
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>
    </AiFeedbackVisibilityProvider>
  );
};
